// @flow
import { range } from 'lodash'
import moment from 'moment'
import * as React from 'react'
import { connect } from 'react-redux'
import type { ContextRouter } from 'react-router'

import Accessibility from '@/accessibility'
import { Button, Calendar, DropDown, DropDownHorizontal, DropdownQuantity, Quantity } from '@/components'
import Strings, {
  AlignmentTypes,
  DISPLAY_DATE_FORMAT,
  DEFAULT_NUMBER_NIGHTS,
  MIN_NUMBER_NIGHTS,
  MAX_NUMBER_NIGHTS,
  Images,
  Routes,
} from '@/constants'
import { classnames, objectToBase64, base64ToObject, mapOptions, dataFromQueryString } from '@/helpers'
import history from '@/helpers/history'
import { Property } from '@/models';
import selectors from '@/selectors'
import { ActionCreators } from '@/redux'

import { SEARCH_BAR_FIELD, VALIDATION } from './constants'
import styles from './styles.scss'

const { Search } = Accessibility

export const ageOptions: Array<{ value: *, label: string }> = mapOptions(range(1, 18).map(n => String(n).padStart(2, '0')))

type PassedProps = {|
  ...$Exact<ContextRouter>,
|}

type DispatchToProps = {|
  fetchProperty: (data: SearchPayload) => void,
|}

export type Props = {
  ...PassedProps,
  ...DispatchToProps,
  search: string,
  pathname: string,
  locations: OptionsType<string>,
  className: string,
  properties: {
    values: Array<Property>,
    isPending: boolean,
    errors: ?ResponseError
  },
  onNewDestination?: (data: boolean) => void,
}

// eslint-disable-next-line max-lines-per-function
const SearchBar = ({locations, match, search, pathname, className, properties, fetchProperty, onNewDestination}: Props) => {
  const { code } = match?.params
  const [fromDate, setFromDate] = React.useState(moment().add(1, 'M').format(DISPLAY_DATE_FORMAT))
  const [toDate, setToDate] = React.useState(moment().add(1, 'M').add(DEFAULT_NUMBER_NIGHTS, 'd').format(DISPLAY_DATE_FORMAT))
  const [adults, setAdults] = React.useState(2)
  const [children, setChildren] = React.useState<Array<number>>([])
  const [destination, setDestination] = React.useState<string | null>(null)
  const [isNewDestination, setIsNewDestination] = React.useState<boolean>(false)
  const [errors, setErrors] = React.useState({
    fromDate: false,
    toDate: false,
    adults: false,
    children: false,
    destination: false
  })

  React.useEffect(() => {
    const { values: propertyValues } = properties
    const { search: query } = dataFromQueryString(search)
    const data = base64ToObject(query)
    const {
      fromDate: searchFromDate,
      toDate: searchToDate,
      adults: searchAdults,
      children: searchChildren,
      destination: searchDestination
    } = data
    if (searchFromDate) setFromDate(searchFromDate)
    if (searchToDate) setToDate(searchToDate)
    if (searchAdults) setAdults(searchAdults)
    if (searchChildren) setChildren(searchChildren)
    if (searchDestination && searchDestination.length > 0) setDestination(searchDestination[0])
    else if (propertyValues.length > 0 && propertyValues[0].region) {
      setDestination(propertyValues[0].region.toString())
      history.replace({
        pathname,
        search: `search=${objectToBase64({
          adults, children, toDate, fromDate,
          // $FlowFixMe
          destination: [`${propertyValues[0].region}`]
        })}`
      })
    }
  }, [search, properties.values, locations])

  React.useEffect(() => {
    // $FlowFixMe
    if (moment(fromDate, DISPLAY_DATE_FORMAT).add(MIN_NUMBER_NIGHTS, 'days') > moment(toDate, DISPLAY_DATE_FORMAT)) {
      setToDate(moment(fromDate, DISPLAY_DATE_FORMAT).add(DEFAULT_NUMBER_NIGHTS, 'days').format(DISPLAY_DATE_FORMAT))
    }
  }, [fromDate])
  
  const validateFields = () => {
    const checks = {
      fromDate: VALIDATION.fromDate(fromDate)
        // $FlowFixMe
        || moment().add(7, 'd') > moment(fromDate, DISPLAY_DATE_FORMAT),
      toDate: VALIDATION.toDate(toDate)
        // $FlowFixMe
        || moment(fromDate, DISPLAY_DATE_FORMAT).add(MIN_NUMBER_NIGHTS, 'd') > moment(toDate, DISPLAY_DATE_FORMAT)
        // $FlowFixMe
        || moment(fromDate, DISPLAY_DATE_FORMAT).add(MAX_NUMBER_NIGHTS, 'd') < moment(toDate, DISPLAY_DATE_FORMAT),
      adults: VALIDATION.adults(adults),
      children: VALIDATION.children(children),
      destination: VALIDATION.destination(destination)
    }
    setErrors(checks)
    return Object.values(checks).every(e => !e)
  }

  const onSearch = () => {
    if (validateFields()) {
      const data = { fromDate, toDate, adults, children, destination: [destination] }
      if (code && !isNewDestination) {
        // $FlowFixMe
        fetchProperty({ ...data, code })
        history.push({
          pathname: Routes.HotelInfo.replace(':code', code),
          search: `search=${objectToBase64({ ...data, code })}&hash=${Math.random()}`
        })
      } else {
        history.push({
          pathname: Routes.SearchResults,
          search: `search=${objectToBase64({ ...data, destination: [destination] })}&hash=${Math.random()}`
        })
      }
      if (destination)
      setIsNewDestination(false)
    }
  }

  const getOption = (value: string | null, options: OptionsType<string>) => {
    const [option] = options.filter(opt => opt.value === value)
    return option
  }

  const handleQuantity = (e: DOMEvent, original: number) => {
    const { value: operator } = e.currentTarget
    return Math.max(...[0, operator === '+' ? original + 1 : original - 1])
  }

  const onDestinationChange = (city: {label: string, value: string}) => {
    if (city.value !== destination) {
      setIsNewDestination(true)
      if (onNewDestination) {
        onNewDestination(true)
      }
      setDestination(city.value)
    }
  }
  const onFromDateChange = (date: string) => setFromDate(date)
  const onToDateChange = (date: string) => setToDate(date)
  const onAdultsChange = (number: number) => setAdults(number)
  const onChildrenChange = (number: number) => setChildren([...children, 1, 1, 1, 1, 1].slice(0, number))
  const onChangeChildAge = (idx, option: {label: string, value: string}) => {
    const updated = [...children]
    updated.splice(idx, 1, parseInt(option.value, 10))
    setChildren(updated)
  }

  const getToDateError = (): string => {
    // $FlowFixMe
    if (moment(fromDate, DISPLAY_DATE_FORMAT).add(MIN_NUMBER_NIGHTS, 'd') > moment(toDate, DISPLAY_DATE_FORMAT)) {
      return Strings.errors.toDate
    }
    // $FlowFixMe
    if (moment(fromDate, DISPLAY_DATE_FORMAT).add(MAX_NUMBER_NIGHTS, 'd') < moment(toDate, DISPLAY_DATE_FORMAT)) {
      return Strings.errors.stayTooLong
    }

    return Strings.cannotBeLeftBlank
  }

  return (
    <div className={classnames(styles.wrapper, className)}>
      <div className={styles.topWrapper}>
        {/* DROPDOWN INPUTS */}
        <div className={styles.dropdownsWrapper}>
          <div className={styles.grid}>
            <div className={styles.row}>
              <div className={classnames(styles.tabInput, styles.fourOne)}>
                <DropDown
                  ariaLabel={Search.To}
                  placeholder={Strings.search.to}
                  key={SEARCH_BAR_FIELD.destination}
                  name={SEARCH_BAR_FIELD.destination}
                  options={locations}
                  onChange={onDestinationChange}
                  value={getOption(destination, locations)}
                  label={Strings.search.destination}
                  error={errors[SEARCH_BAR_FIELD.destination] && Strings.cannotBeLeftBlank}
                />
              </div>
              <div className={classnames(styles.tabInput, styles.fourFourTwo)}>
                <Calendar
                  ariaLabel={Search.FromDate}
                  placeholder={Strings.search.depart}
                  key={SEARCH_BAR_FIELD.fromDate}
                  name={SEARCH_BAR_FIELD.fromDate}
                  onChange={onFromDateChange}
                  value={fromDate}
                  label={Strings.search.departing}
                  error={errors[SEARCH_BAR_FIELD.fromDate] && (
                    // $FlowFixMe
                    moment().add(7, 'd') > moment(fromDate)
                      ? Strings.errors.toDate
                      : Strings.cannotBeLeftBlank)}
                  calendarAlignment={AlignmentTypes.left}
                  minDate={moment().add(7, 'days')}
                  maxValue={1}
                  maxValueUnit="year"
                />
              </div>
              <div className={classnames(styles.tabInput, styles.fourFourTwo)}>
                <Calendar
                  ariaLabel={Search.ToDate}
                  placeholder={Strings.search.return}
                  key={SEARCH_BAR_FIELD.toDate}
                  name={SEARCH_BAR_FIELD.toDate}
                  onChange={onToDateChange}
                  value={toDate}
                  label={Strings.search.returning}
                  error={errors[SEARCH_BAR_FIELD.toDate] && getToDateError()}
                  calendarAlignment={AlignmentTypes.right}
                  minDate={moment(fromDate, DISPLAY_DATE_FORMAT).add(MIN_NUMBER_NIGHTS, 'days')}
                  maxValue={1}
                  maxValueUnit="year"
                />
              </div>
              <div
                className={classnames(styles.tabletAndMobileOnly, styles.tabInput, styles.sixFourTwo)}
              >
                <DropdownQuantity
                  ariaLabel={Search.Adult}
                  placeholder={Strings.search.zero}
                  key={SEARCH_BAR_FIELD.adults}
                  name={SEARCH_BAR_FIELD.adults}
                  onChange={onAdultsChange}
                  value={adults}
                  label={Strings.search.adults}
                  error={errors[SEARCH_BAR_FIELD.adults] && Strings.cannotBeLeftBlank}
                  limit={4}
                />
              </div>
              <div
                className={classnames(styles.tabletAndMobileOnly, styles.tabInput, styles.sixFourTwo)}
              >
                <DropdownQuantity
                  ariaLabel={Search.Children}
                  placeholder={Strings.search.zero}
                  key={SEARCH_BAR_FIELD.children}
                  name={SEARCH_BAR_FIELD.children}
                  onChange={onChildrenChange}
                  value={children.length}
                  label={Strings.search.children}
                  error={errors[SEARCH_BAR_FIELD.children] && Strings.cannotBeLeftBlank}
                  limit={4}
                />
              </div>
            </div>
          </div>
        </div>
        {/* SEARCH BUTTON */}
        <div className={styles.searchButtonContainer}>
          <div className={classnames(styles.tabInput, styles.searchButton)}>
            <Button
              title={Strings.search.search}
              imgUrls={{ default: Images.searchIcon, selected: Images.searchPressed }}
              onClick={onSearch}
              ariaLabel={Search.Search}
              className={styles.squareButton}
            />
          </div>
        </div>
        {/* CHILDREN AGE INPUTS */}
        <div className={classnames(styles.dropdownsWrapper, styles.tabletAndMobileOnly)}>
          <div className={styles.grid}>
            <div className={styles.row}>
              {
                children.map((age, idx) => (
                  <div
                    className={classnames(styles.tabInput, styles.fourFourTwoChildren)}
                    key={`row-${age}`}
                  >
                    <DropDown
                      ariaLabel={Search.ChildAge}
                      placeholder={Strings.search.age}
                      name={`${SEARCH_BAR_FIELD.children}-${age}`}
                      options={ageOptions}
                      onChange={value => onChangeChildAge(idx, value)}
                      value={getOption(String(children[idx]).padStart(2, '0'), ageOptions)}
                      label={`${Strings.search.child} ${idx + 1} ${Strings.search.age}`}
                      key={`${SEARCH_BAR_FIELD.children}-${age}`}
                    />
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
      {/* COMBINED ADULTS/CHILDREN INPUT */}
      <div className={styles.includedFlightWrapper}>
        <div className={styles.bottomRow}>
          <div className={classnames(styles.desktopOnly, styles.tabInput, styles.complicatedInput)}>
            <DropdownQuantity
              ariaLabel={Search.Adult}
              placeholder={Strings.search.zero}
              key={SEARCH_BAR_FIELD.adults}
              name={SEARCH_BAR_FIELD.adults}
              onChange={onAdultsChange}
              value={`${adults} ${Strings.search.adultPlural(adults)}, ${children.length} ${Strings.search.childPlural(children.length)}`}
              type="text"
              disabled
              label={Strings.search.travelers}
              error={errors[SEARCH_BAR_FIELD.adults] && Strings.cannotBeLeftBlank}
              dropdown={(
                <div className={styles.optionsContainer}>
                  <Quantity
                    ariaLabel={`${Strings.search.adult}-quantity`}
                    onClick={e => onAdultsChange(handleQuantity(e, adults))}
                    value={adults}
                    label={Strings.search.adult}
                    key="adults"
                    className={styles.quantity}
                  />
                  <Quantity
                    ariaLabel={`${Strings.search.children}-quantity`}
                    onClick={e => onChildrenChange(handleQuantity(e, children.length))}
                    value={children.length}
                    label={Strings.search.children}
                    key="children"
                    className={styles.quantity}
                  />
                  {
                    children.map((age, idx) => (
                      <DropDownHorizontal
                        ariaLabel={Search.ChildAge}
                        placeholder={Strings.search.age}
                        name={`${SEARCH_BAR_FIELD.children}-${idx}`}
                        options={ageOptions}
                        onChange={value => onChangeChildAge(idx, value)}
                        value={getOption(String(children[idx]).padStart(2, '0'), ageOptions)}
                        label={`${Strings.search.child} ${idx + 1} ${Strings.search.age}`}
                        key={`${SEARCH_BAR_FIELD.children}-${age}`}
                      />
                    ))
                  }
                </div>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const stateProps = (state: StoreState) => ({
  // $FlowFixMe
  search: state.router.location.search || '',
  // $FlowFixMe
  pathname: state.router.location.pathname || '',
  locations: selectors.apple.sortedDropDownLocations(state),
  properties: selectors.apple.getProperties(state),
})

const dispatchProps = (dispatch: Dispatch): DispatchToProps => ({
  fetchProperty: (data: SearchPayload) =>
    dispatch(ActionCreators.apple.searchPropertiesRequest.dispatch(data))
})

// $FlowFixMe
export default connect(stateProps, dispatchProps)(SearchBar)
